import {
    ActionButton, Flex, Text, View
} from '@adobe/react-spectrum';
import React, {
    FC, useCallback, useEffect, useMemo, useState
} from 'react';
import SelectionMove from '@spectrum-icons/workflow/SelectionMove';

import { deg2rad, rad2deg } from '~/Utils/math';
import { InputSliderField } from '~/Components/Input/inputSliderField';
import { _camera, _mouseCatcher, _system } from '~/Services/Studio/studioScene';
import { NakerMouseEvent } from '~/Scene/Catchers/mouseCatcher';
import { IArcCameraAngles } from '~/Scene/System/systemCamera';

interface ISlidersProps {
    setShow: (x: boolean) => void;
}

export const CanvasCameraSliders: FC<ISlidersProps> = ({ setShow }) => {
    const [angles, setAngles] = useState<IArcCameraAngles>({ alpha: 0, beta: 0, radius: 0 });

    const maxZoom = useMemo(() => _camera.getMaxZoom(), []);

    const onChange = useCallback((axis: string, change) => {
        const newAngles: IArcCameraAngles = {
            alpha: axis === 'alpha' ? change : angles.alpha,
            beta: axis === 'beta' ? change : angles.beta,
            radius: axis === 'radius' ? change : angles.radius,
        };
        _system.setAlphaBetaRadius({
            alpha: (deg2rad(newAngles.alpha)),
            beta: deg2rad(newAngles.beta),
            radius: newAngles.radius
        });
        setAngles({ ...newAngles });
    }, [angles]);

    const onDragEvent = useCallback(() => {
        const angles = _system.getAlphaBetaRadius();
        const newAngles = {
            alpha: Math.round(rad2deg(angles.alpha)),
            beta: Math.round(rad2deg(angles.beta)),
            radius: angles.radius
        };
        setAngles({ ...newAngles });
    }, [angles]);

    useEffect(() => {
        onDragEvent();
        _mouseCatcher.on(NakerMouseEvent.Drag, onDragEvent);
        _mouseCatcher.on(NakerMouseEvent.Preset, onDragEvent);
        _mouseCatcher.on(NakerMouseEvent.Wheel, onDragEvent);
        return () => {
            _mouseCatcher.off(NakerMouseEvent.Drag, onDragEvent);
            _mouseCatcher.off(NakerMouseEvent.Preset, onDragEvent);
            _mouseCatcher.off(NakerMouseEvent.Wheel, onDragEvent);
        };
    }, []);

    return (
        <Flex
            flex
            alignItems="center"
            direction="column"
            gap="size-100"
            position="absolute"
            zIndex={2}
            top="10px"
            left="10px"
        >
            <View backgroundColor="gray-100" borderRadius="medium" width="100%">
                <Flex direction="row">
                    <ActionButton onPress={() => setShow(false)} isQuiet>
                        <SelectionMove />
                        <Text>
                            Camera position
                        </Text>
                    </ActionButton>

                </Flex>
                <Flex direction="column" alignItems="center" gap="size-100" margin="size-100">
                    <InputSliderField
                        min={0}
                        max={360}
                        step={1}
                        label="Horizontal"
                        width="size-2000"
                        defaultValue={0}
                        value={angles.alpha}
                        onChange={(val) => onChange('alpha', val)}
                    />
                    <InputSliderField
                        min={0}
                        max={180}
                        step={1}
                        label="Vertical"
                        width="size-2000"
                        defaultValue={0}
                        value={angles.beta}
                        onChange={(val) => onChange('beta', val)}
                    />
                    <InputSliderField
                        min={0}
                        max={maxZoom}
                        step={0.1}
                        label="Distance"
                        width="size-2000"
                        defaultValue={0}
                        value={angles.radius}
                        onChange={(val) => onChange('radius', val)}
                    />
                </Flex>
            </View>
        </Flex>
    );
};
